import { TableCellTypes, Radio } from '@carboledger/ui';
import {
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
  makeAssignQuantityCell,
} from '../tableCells';
import { shipmentUnits } from './linkUnits';

// Columns for the Link contracts modal.

const renderUnitTypeCell = makeUnitTypeCell((t, row) =>
  shipmentUnits.buildRowUnitOptions(t, row.byUnit),
);
const renderAssignedQuantity = makeUnitScopedCell('assignedQuantity');
const renderContractedQuantity = makeUnitScopedCell('contractedQuantity');
const renderOutstandingQuantity = makeUnitScopedCell('outstandingQuantity');
const renderShipmentQuantity = makeUnitScopedCell('shipmentQuantity');
const renderUnassignedQuantity = makeUnitScopedCell('unassignedQuantity');
const renderReceivedDeclarationQuantity = makeUnitScopedCell('receivedDeclarationQuantity');

const unitTypeColumn = (t, headerSelect) => ({
  id: 'unitType',
  header: t('FIELD_UNIT_TYPE', {
    unit: shipmentUnits.unitTypeLabel(t, headerSelect.value),
  }),
  type: TableCellTypes.custom,
  width: 252,
  cell: renderUnitTypeCell,
  headerSelect,
});

const assignQuantityColumn = (t, renderAssign) => ({
  id: 'assignQuantity',
  header: t('LINK_ASSIGN_QUANTITY'),
  type: TableCellTypes.custom,
  width: 240,
  cell: renderAssign,
});

// Sustainability columns, identical in every step and both flows.
const sustainabilityColumns = (t) => [
  {
    id: 'ghgEmissions',
    header: t('FIELD_GHG_EMISSIONS'),
    type: TableCellTypes.text,
    width: 180,
  },
  {
    id: 'ghgEmissionSavings',
    header: t('FIELD_GHG_EMISSION_SAVINGS'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: 'rawMaterialType',
    header: t('FIELD_RAW_MATERIAL_TYPE'),
    type: TableCellTypes.text,
    width: 200,
  },
  {
    id: 'countryOfOrigin',
    header: t('FIELD_COUNTRY_OF_ORIGIN'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: 'feedstockCategory',
    header: t('FIELD_FEEDSTOCK_CATEGORY'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: 'allocationPreference',
    header: t('FIELD_ALLOCATION_PREFERENCE'),
    type: TableCellTypes.text,
    width: 180,
  },
];

// The contract-row columns every step shares, after its own leading columns.
const contractQuantityColumns = (t, headerSelect) => [
  {
    id: 'totalShipmentLinked',
    header: t('FIELD_TOTAL_SHIPMENT_LINKED'),
    type: TableCellTypes.text,
    width: 180,
  },
  unitTypeColumn(t, headerSelect),
  {
    id: 'assignedQuantity',
    header: t('FIELD_ASSIGNED_QUANTITY_CONTRACT'),
    type: TableCellTypes.custom,
    width: 220,
    cell: renderAssignedQuantity,
  },
  {
    id: 'contractedQuantity',
    header: t('FIELD_CONTRACTED_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    cell: renderContractedQuantity,
  },
  {
    id: 'shipmentQuantity',
    header: t('FIELD_SHIPMENT_QUANTITY'),
    type: TableCellTypes.custom,
    width: 180,
    cell: renderShipmentQuantity,
  },
  {
    id: 'outstandingQuantity',
    header: t('FIELD_OUTSTANDING_QUANTITY'),
    type: TableCellTypes.custom,
    width: 200,
    cell: renderOutstandingQuantity,
  },
];

const contractTailColumns = (t, onOpenDiscrepancy) => [
  ...sustainabilityColumns(t),
  {
    id: 'deliverBy',
    header: t('FIELD_DELIVER_BY'),
    type: TableCellTypes.text,
    width: 150,
  },
  {
    id: 'discrepancies',
    header: t('FIELD_DISCREPANCIES'),
    type: TableCellTypes.custom,
    width: 180,
    cell: makeDiscrepanciesCell(onOpenDiscrepancy),
  },
];

/**
 * Step 1 — the contracts to pick from. `selectMode` is 'multi' (the Table's own
 * checkbox column) or 'single', which needs an app-owned radio column instead.
 */
export const buildLinkContractsStep1Columns = (
  t,
  { headerSelect, selectMode, selectedId, onSelect, onOpenDiscrepancy },
) => {
  const columns = [
    {
      id: 'contractId',
      header: t('FIELD_CONTRACT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    ...contractQuantityColumns(t, headerSelect),
    ...contractTailColumns(t, onOpenDiscrepancy),
  ];

  if (selectMode === 'multi') return columns;

  // One contract serves every selected shipment, so the pick is a radio the app owns.
  // TODO: name the control once the library Radio takes an aria-label.
  const RadioCell = ({ row }) => (
    <Radio
      name="linkContract"
      value={String(row.id)}
      checked={selectedId === row.id}
      onChange={() => onSelect(row.id)}
    />
  );
  return [
    {
      id: '__pick__',
      header: '',
      type: TableCellTypes.custom,
      width: 56,
      resizable: false,
      reorderable: false,
      cell: (ctx) => <RadioCell row={ctx.row} />,
    },
    ...columns,
  ];
};

// Step 2 (single-shipment flow) — the chosen contracts, each with an assign-quantity input.
export const buildLinkContractsStep2Columns = (
  t,
  { headerSelect, assignments, onAssign, onOpenDiscrepancy },
) => {
  const renderAssign = makeAssignQuantityCell(assignments, onAssign);

  return [
    {
      id: 'contractId',
      header: t('FIELD_CONTRACT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    assignQuantityColumn(t, renderAssign),
    ...contractQuantityColumns(t, headerSelect),
    ...contractTailColumns(t, onOpenDiscrepancy),
  ];
};

/**
 * Step 2 (multi-shipment flow) — one row per selected SHIPMENT. The rows are the
 * shipments table's own view models, so only the assign input is new.
 */
export const buildLinkShipmentsStep2Columns = (
  t,
  { headerSelect, assignments, onAssign, onOpenDiscrepancy },
) => {
  const renderAssign = makeAssignQuantityCell(assignments, onAssign);

  return [
    {
      id: 'shipmentId',
      header: t('FIELD_SHIPMENT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    assignQuantityColumn(t, renderAssign),
    unitTypeColumn(t, headerSelect),
    {
      id: 'totalContractsLinked',
      header: t('FIELD_TOTAL_CONTRACTS_LINKED'),
      type: TableCellTypes.text,
      width: 180,
    },
    {
      id: 'unassignedQuantity',
      header: t('FIELD_UNASSIGNED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderUnassignedQuantity,
    },
    {
      id: 'shipmentQuantity',
      header: t('FIELD_SHIPMENT_QUANTITY'),
      type: TableCellTypes.custom,
      width: 180,
      cell: renderShipmentQuantity,
    },
    {
      id: 'receivedDeclarationQuantity',
      header: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
      type: TableCellTypes.custom,
      width: 240,
      cell: renderReceivedDeclarationQuantity,
    },
    ...sustainabilityColumns(t),
    {
      id: 'dateOfDispatch',
      header: t('FIELD_DATE_OF_DISPATCH'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'dateOfDischarge',
      header: t('FIELD_DATE_OF_DISCHARGE'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'discrepancies',
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 180,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ];
};
